if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.prefetchCache = new Map();
      this.snapshotStorageKey = 'a4cProductListSnapshotV9';
      this.init();
      document.addEventListener('on:bfcache:load-restore', this.handleBfcacheRestore.bind(this));
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.controller = null;
      this.isFetching = false;
      this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
      this.loadMoreClickHandler = this.loadMoreClickHandler || this.handleLoadMoreClick.bind(this);
      this.prefetchHandler = this.prefetchHandler || this.prefetchNextPage.bind(this);

      this.addEventListener('click', this.resultClickHandler);

      const restoreState = this.getCollectionRestoreState();
      let restoredFromSnapshot = false;

      this.refreshPaginationReferences();

      if (restoreState && this.results.length < restoreState.resultCount) {
        restoredFromSnapshot = this.restoreCollectionSnapshot(restoreState);
      }

      this.refreshPaginationReferences();
      this.bindLoadMoreButton();

      if (restoreState) {
        this.beginRestoreMode();

        if (this.results.length >= restoreState.resultCount || restoredFromSnapshot) {
          this.restoreScrollPosition(restoreState);
          this.finishRestore();
        } else {
          this.restorePromise = this.restoreCollectionState(restoreState)
            .catch((error) => {
              console.log('Unable to restore collection position', error);
            })
            .finally(() => {
              this.restorePromise = null;
              this.finishRestore();
            });
        }
      } else {
        this.setupPaginationObserver();
        this.schedulePrefetch();
      }
    }

    destroy() {
      if (this.intersectingTimer) clearInterval(this.intersectingTimer);
      if (this.prefetchTimer) clearTimeout(this.prefetchTimer);
      if (this.restoreCleanupTimer) clearTimeout(this.restoreCleanupTimer);
      if (this.paginationObserver) this.paginationObserver.disconnect();
      if (this.controller) this.controller.abort();

      this.unbindLoadMoreButton();
      this.removeEventListener('click', this.resultClickHandler);

      this.paginationObserver = null;
      this.intersectingTimer = null;
      this.prefetchTimer = null;
    }

    handleBfcacheRestore() {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }

      this.refreshPaginationReferences();
    }

    finishRestore() {
      this.endRestoreMode();
      this.clearCollectionRestoreState();
      this.setupPaginationObserver();
      this.schedulePrefetch();
    }

    refreshPaginationReferences() {
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      this.results = this.querySelectorAll('.js-pagination-result');
    }

    bindLoadMoreButton() {
      if (!this.loadMoreButton) return;

      this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
      this.loadMoreButton.addEventListener('pointerenter', this.prefetchHandler, { passive: true });
      this.loadMoreButton.addEventListener('focus', this.prefetchHandler);
      this.loadMoreButton.addEventListener('touchstart', this.prefetchHandler, { passive: true });
    }

    unbindLoadMoreButton() {
      if (!this.loadMoreButton) return;

      this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      this.loadMoreButton.removeEventListener('pointerenter', this.prefetchHandler);
      this.loadMoreButton.removeEventListener('focus', this.prefetchHandler);
      this.loadMoreButton.removeEventListener('touchstart', this.prefetchHandler);
    }

    setupPaginationObserver() {
      if (!this.infiniteScroll || !this.pagination || !this.pagination.isConnected) return;
      if (this.pagination.dataset.isMoreResults !== 'true' || !this.results.length) return;

      if (this.paginationObserver) this.paginationObserver.disconnect();
      if (this.intersectingTimer) clearInterval(this.intersectingTimer);

      this.paginationObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.loadMore();
              this.intersectingTimer = setInterval(() => this.loadMore(), 1000);
            } else if (this.intersectingTimer) {
              clearInterval(this.intersectingTimer);
              this.intersectingTimer = null;
            }
          });
        },
        { rootMargin: `${window.innerHeight * 1.5}px 0px` }
      );

      this.paginationObserver.observe(this.pagination);
    }

    schedulePrefetch() {
      if (!this.loadMoreButton || !this.loadMoreButton.href) return;
      if (this.prefetchTimer) clearTimeout(this.prefetchTimer);

      const run = () => this.prefetchNextPage();

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 1200 });
      } else {
        this.prefetchTimer = setTimeout(run, 700);
      }
    }

    prefetchNextPage() {
      const url = this.loadMoreButton?.href;
      if (!url || this.prefetchCache.has(url)) return;

      const request = fetch(url, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .catch(() => {
          this.prefetchCache.delete(url);
          return null;
        });

      this.prefetchCache.set(url, request);
    }

    async getPageHtml(url) {
      const cachedRequest = this.prefetchCache.get(url);

      if (cachedRequest) {
        const cachedHtml = await cachedRequest;
        this.prefetchCache.delete(url);
        if (cachedHtml) return cachedHtml;
      }

      const response = await fetch(url, {
        signal: this.controller.signal,
        credentials: 'same-origin'
      });

      if (!response.ok) throw new Error(response.status);
      return response.text();
    }

    handleLoadMoreClick(evt) {
      evt.preventDefault();
      this.loadMore();
    }

    handleResultClick(evt) {
      const link = evt.target.closest('a[href]');
      const result = evt.target.closest('.js-pagination-result');

      if (!link || !result) return;

      const isModifiedClick = evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey;
      const opensNewContext = link.target === '_blank';

      if (!evt.defaultPrevented && !isModifiedClick && !opensNewContext) {
        try {
          const destination = new URL(link.href, window.location.href);
          const isProductLink =
            destination.origin === window.location.origin && destination.pathname.includes('/products/');

          if (isProductLink) {
            this.saveCollectionState(link, result);
          }
        } catch (error) {
          console.log('Unable to save collection position', error);
        }
      }

      if (this.controller) this.controller.abort();
    }

    getCollectionStateKey() {
      return `${window.location.pathname}${window.location.search}`;
    }

    saveCollectionState(link, result) {
      const currentState = history.state && typeof history.state === 'object' ? history.state : {};
      const resultIndex = Array.from(this.results).indexOf(result);
      const destination = new URL(link.href, window.location.href);

      history.replaceState(
        {
          ...currentState,
          a4cProductListRestore: {
            key: this.getCollectionStateKey(),
            resultCount: this.results.length,
            resultIndex,
            productPath: destination.pathname,
            cardViewportTop: result.getBoundingClientRect().top,
            scrollY: window.scrollY,
            documentHeight: Math.max(
              document.documentElement.scrollHeight,
              document.body ? document.body.scrollHeight : 0
            ),
            viewportHeight: window.innerHeight,
            savedAt: Date.now()
          }
        },
        '',
        window.location.href
      );
    }

    getCollectionRestoreState() {
      const state = history.state;
      if (!state || typeof state !== 'object') return null;

      const restoreState = state.a4cProductListRestore;
      if (!restoreState || restoreState.key !== this.getCollectionStateKey()) return null;
      if (!Number.isFinite(restoreState.resultCount) || restoreState.resultCount < 1) return null;

      return restoreState;
    }

    clearCollectionRestoreState() {
      const currentState = history.state;
      if (!currentState || typeof currentState !== 'object' || !currentState.a4cProductListRestore) return;

      const nextState = { ...currentState };
      delete nextState.a4cProductListRestore;
      history.replaceState(nextState, '', window.location.href);
    }

    queueSnapshotSave() {
      const save = () => this.saveCollectionSnapshot();

      if ('queueMicrotask' in window) {
        window.queueMicrotask(save);
      } else {
        Promise.resolve().then(save);
      }
    }

    saveCollectionSnapshot() {
      if (!('sessionStorage' in window)) return;

      try {
        const payload = {
          key: this.getCollectionStateKey(),
          resultCount: this.results?.length || 0,
          savedAt: Date.now(),
          html: this.innerHTML
        };

        sessionStorage.setItem(this.snapshotStorageKey, JSON.stringify(payload));
      } catch (error) {
        try {
          sessionStorage.removeItem(this.snapshotStorageKey);
        } catch (storageError) {
          return;
        }
      }
    }

    restoreCollectionSnapshot(state) {
      if (!state || !('sessionStorage' in window)) return false;

      try {
        const rawSnapshot = sessionStorage.getItem(this.snapshotStorageKey);
        if (!rawSnapshot) return false;

        const snapshot = JSON.parse(rawSnapshot);
        const isFresh = Number.isFinite(snapshot.savedAt) && Date.now() - snapshot.savedAt < 30 * 60 * 1000;
        const matchesState = snapshot.key === state.key && snapshot.resultCount >= state.resultCount;

        if (!isFresh || !matchesState || !snapshot.html) return false;

        this.innerHTML = snapshot.html;
        this.refreshPaginationReferences();
        return this.results.length >= state.resultCount;
      } catch (error) {
        return false;
      }
    }

    beginRestoreMode() {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }

      const earlyRestore = window.__a4cEarlyCollectionRestore;

      this.previousOverflowAnchor = earlyRestore
        ? earlyRestore.overflowAnchor
        : document.documentElement.style.overflowAnchor;
      this.previousScrollBehavior = earlyRestore
        ? earlyRestore.scrollBehavior
        : document.documentElement.style.scrollBehavior;
      this.previousBodyMinHeight = earlyRestore
        ? earlyRestore.bodyMinHeight
        : document.body.style.minHeight;

      document.documentElement.style.overflowAnchor = 'none';
      document.documentElement.style.scrollBehavior = 'auto';
    }

    endRestoreMode() {
      if (this.restoreCleanupTimer) clearTimeout(this.restoreCleanupTimer);

      this.restoreCleanupTimer = setTimeout(() => {
        if ('scrollRestoration' in history) {
          history.scrollRestoration = 'auto';
        }

        if (this.previousOverflowAnchor !== undefined) {
          document.documentElement.style.overflowAnchor = this.previousOverflowAnchor;
          this.previousOverflowAnchor = undefined;
        }

        if (this.previousScrollBehavior !== undefined) {
          document.documentElement.style.scrollBehavior = this.previousScrollBehavior;
          this.previousScrollBehavior = undefined;
        }

        if (this.previousBodyMinHeight !== undefined) {
          document.body.style.minHeight = this.previousBodyMinHeight;
          this.previousBodyMinHeight = undefined;
        }

        delete window.__a4cEarlyCollectionRestore;
        this.restoreCleanupTimer = null;
      }, 250);
    }

    async restoreCollectionState(state) {
      const targetResultCount = state.resultCount;
      let restoreLoads = 0;
      const maxRestoreLoads = 100;

      while (
        this.isConnected &&
        this.results.length < targetResultCount &&
        this.pagination &&
        this.pagination.isConnected &&
        this.loadMoreButton &&
        restoreLoads < maxRestoreLoads
      ) {
        const previousResultCount = this.results.length;
        await this.loadMore({ restoring: true });

        if (!this.isConnected) return;
        if (this.results.length <= previousResultCount) break;
        restoreLoads += 1;
      }

      this.saveCollectionSnapshot();
      this.restoreScrollPosition(state);
    }

    findRestoreTarget(state) {
      if (state.productPath) {
        const productLinks = this.querySelectorAll('.js-pagination-result a[href]');

        for (const productLink of productLinks) {
          try {
            const productUrl = new URL(productLink.href, window.location.href);
            if (productUrl.pathname === state.productPath) {
              return productLink.closest('.js-pagination-result');
            }
          } catch (error) {
            continue;
          }
        }
      }

      if (Number.isInteger(state.resultIndex) && state.resultIndex >= 0) {
        return this.results[state.resultIndex] || null;
      }

      return null;
    }

    restoreScrollPosition(state) {
      if (Number.isFinite(state.scrollY)) {
        window.scrollTo({
          top: Math.max(0, state.scrollY),
          left: 0,
          behavior: 'auto'
        });
        return;
      }

      const targetResult = this.findRestoreTarget(state);

      if (targetResult) {
        const targetTop = targetResult.getBoundingClientRect().top + window.pageYOffset;
        const savedViewportTop = Number.isFinite(state.cardViewportTop) ? state.cardViewportTop : 120;

        window.scrollTo({
          top: Math.max(0, targetTop - savedViewportTop),
          left: 0,
          behavior: 'auto'
        });
      }
    }

    async loadMore({ restoring = false } = {}) {
      if (this.isFetching || this.dataset.pauseInfiniteScroll !== 'false') return;
      if (!this.loadMoreButton || !this.loadMoreButton.href) return;

      this.isFetching = true;
      this.controller = new AbortController();
      const requestUrl = this.loadMoreButton.href;

      try {
        if (!restoring) {
          this.loadMoreButton.classList.add('is-loading');
          this.loadMoreButton.setAttribute('aria-disabled', 'true');
        }

        if (this.pagination?.dataset.paginationStyle === 'traditional') {
          this.pagination.classList.add('is-loading');
        }

        const html = await this.getPageHtml(requestUrl);

        if (this.dataset.pauseInfiniteScroll === 'true') {
          throw new Error('Infinite scroll was interrupted.');
        }

        const tmpl = document.createElement('template');
        tmpl.innerHTML = html;

        const newResults = Array.from(tmpl.content.querySelectorAll('.js-pagination-result'));
        const lastResult = this.results[this.results.length - 1];

        if (!lastResult || !newResults.length) {
          throw new Error('No additional pagination results found.');
        }

        const fragment = document.createDocumentFragment();
        newResults.forEach((result) => fragment.appendChild(result));
        lastResult.after(fragment);
        this.results = this.querySelectorAll('.js-pagination-result');

        const newPagination = tmpl.content.querySelector('.js-pagination');

        if (newPagination && newPagination.dataset.isMoreResults === 'true') {
          this.unbindLoadMoreButton();
          this.pagination.innerHTML = newPagination.innerHTML;
          this.loadMoreButton = this.querySelector('.js-pagination-load-more');
          this.bindLoadMoreButton();

          if (!restoring) {
            this.queueSnapshotSave();
            this.schedulePrefetch();
          }
        } else {
          this.unbindLoadMoreButton();
          this.pagination?.remove();
          this.pagination = null;
          this.loadMoreButton = null;

          if (this.intersectingTimer) {
            clearInterval(this.intersectingTimer);
            this.intersectingTimer = null;
          }

          if (this.paginationObserver) this.paginationObserver.disconnect();
          document.querySelectorAll('.js-when-paginated-only').forEach((elem) => elem.remove());

          if (!restoring) this.queueSnapshotSave();
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.log('Unable to fetch next page of results', error);
        }

        if (this.intersectingTimer) {
          clearInterval(this.intersectingTimer);
          this.intersectingTimer = null;
        }
      } finally {
        this.controller = null;

        if (this.loadMoreButton) {
          this.loadMoreButton.classList.remove('is-loading');
          this.loadMoreButton.removeAttribute('aria-disabled');
        }

        this.isFetching = false;

        if (this.pagination?.dataset.paginationStyle === 'traditional') {
          this.pagination.classList.remove('is-loading');
        }
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
